import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import LocationAddNewCN from '../LocationAddNewCN'
import LocationsCards from './LocationsCards'
import LocationsTabelCN from './LocationsTableCN'
import LocationsMapPanel from './LocationsMapPanel'
import LocationActionSheet from './LocationActionSheet'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import Layout from 'components/Layout'
import Message from 'components/Message'
import Spinner from 'components/Spinner'
import useTable from 'components/controls/useTable'
import LocationsEmptyState from 'components/LocationsEmptyState'
import { ViewToggle } from 'components/ViewToggle'
import { LayoutGrid, List, Map, MapPin, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react'
import * as _ from 'styles/helpers'
import useCapabilities from 'hooks/useCapabilities'
import { createLocation, initializeActiveLocations } from 'slices/locationSlice'

const headerCells = [
	{ id: 'Image', label: '', disableSorting: true },
	{ id: 'assetName', label: 'Location' },
	{ id: 'owner.name', label: 'Originator', disableSorting: true },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Postal Code' },
	{ id: 'update', label: 'x' },
]

const Locations = ({ viewMode, onViewModeChange, isMobile, listOnly = isMobile }) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { canCreateAssets } = useCapabilities()

	const searchInputRef = useRef(null)
	const mapRef = useRef(null)
	const [openPopup, setOpenPopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')
	// The add-location popup widens for its map pane. A 700px map is too
	// cramped to place a pin accurately, which is the point of the pane.
	const [addLocationMode, setAddLocationMode] = useState('form')
	const [searchInput, setSearchInput] = useState('')

	// Location edit slider, opened from the map view's cards and markers.
	const [sheetLocation, setSheetLocation] = useState(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	// Card view sort state
	const [cardSortBy, setCardSortBy] = useState(null)
	const [cardSortOrder, setCardSortOrder] = useState('asc')

	const cardSortFields = [
		{ id: 'assetName', label: 'Location' },
		{ id: 'city', label: 'City' },
		{ id: 'stateProvince', label: 'State' },
		{ id: 'postalCode', label: 'Postal Code' },
		{ id: 'updatedAt', label: 'Date Updated' },
	]

	const handleCardSort = fieldId => {
		if (cardSortBy === fieldId) {
			setCardSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setCardSortBy(fieldId)
			setCardSortOrder('asc')
		}
	}

	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})

	const {
		entities: locations,
		error,
		ids: locationIds,
		isLoading,
		isInitialized,
	} = useSelector(state => state.locations)

	const records = locationIds.map(id => locations[id])

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin

	const userId = user._id

	useEffect(() => {
		dispatch(initializeActiveLocations())
	}, [dispatch, isInitialized])

	const recordCount = records.length

	const { recordsAfterPagingAndSorting } = useTable(
		records,
		headerCells,
		filterFn,
		true // noPages — TW table and cards both handle their own pagination
	)

	const locationAdd = async (location, resetForm) => {
		const newLocation = location

		newLocation.owner = userId

		if (newLocation.locationType) {
			const locationType = newLocation.locationType.toLocaleLowerCase()
			const newAssetType = locationType.split(' ')

			if (newAssetType[1]) newLocation.assetType = newAssetType[1]
		}
		await dispatch(createLocation({ newLocation }))
		dispatch(initializeActiveLocations())
		resetForm()
		setOpenPopup(false)
	}

	const handelLocationAdd = () => {
		setPopupTitle('New Location')
		setAddLocationMode('form')
		setOpenPopup(true)
	}

	const handleSearchAll = e => {
		const { value } = e.target
		setSearchInput(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
						(x.owner.name && x.owner.name.toLowerCase().includes(lowerValue)) ||
						(x.address && x.address.toLowerCase().includes(lowerValue)) ||
						(x.city && x.city.toLowerCase().includes(lowerValue)) ||
						(x.stateProvince && x.stateProvince.toLowerCase().includes(lowerValue)) ||
						(x.postalCode && x.postalCode.toLowerCase().includes(lowerValue))
				)
			},
		})
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	// The map panel takes the filtered/sorted records whole — it owns the
	// placement rules and reports anything it could not place.
	const mapLocations = useMemo(() => recordsAfterPagingAndSorting(), [recordsAfterPagingAndSorting])

	const savedMapView = useMemo(() => {
		try {
			const raw = sessionStorage.getItem('locationsMapView')
			if (raw) {
				sessionStorage.removeItem('locationsMapView')
				return JSON.parse(raw)
			}
		} catch {
			/* ignore */
		}
		return null
	}, [])

	// Card and marker clicks both open the location's edit slider in place — the
	// same sheet the Cards and Table views open, so the three views of this
	// screen behave identically. The current view is still stashed so that
	// leaving the screen by any other route and coming back lands where the map
	// was left.
	const handleSelectMapLocation = useCallback(location => {
		if (!location) return
		const viewState = mapRef.current?.getViewState?.()
		if (viewState) {
			sessionStorage.setItem('locationsMapView', JSON.stringify(viewState))
		}
		setSheetLocation(location)
		setSheetOpen(true)
	}, [])

	// The marker popup's second action: leave the map entirely for the
	// location's own screen. The view is stashed the same way, so coming back
	// lands on the map where it was left rather than re-fitting.
	const handleGotoMapLocation = useCallback(
		location => {
			if (!location?._id) return
			const viewState = mapRef.current?.getViewState?.()
			if (viewState) {
				sessionStorage.setItem('locationsMapView', JSON.stringify(viewState))
			}
			// `/locations/:id` renders the shell with an empty sub-route area.
			// The location's own page is the first tab, Projects. Land there.
			navigate(`/locations/${location._id}/projects`)
		},
		[navigate]
	)

	// Sort cards
	const sortedCardLocations = useMemo(() => {
		const locs = recordsAfterPagingAndSorting()
		if (!cardSortBy) return locs
		return [...locs].sort((a, b) => {
			let aVal = a[cardSortBy] ?? ''
			let bVal = b[cardSortBy] ?? ''
			if (cardSortBy === 'updatedAt') {
				aVal = new Date(aVal).getTime() || 0
				bVal = new Date(bVal).getTime() || 0
			} else {
				aVal = aVal.toString().toLowerCase()
				bVal = bVal.toString().toLowerCase()
			}
			if (aVal < bVal) return cardSortOrder === 'asc' ? -1 : 1
			if (aVal > bVal) return cardSortOrder === 'asc' ? 1 : -1
			return 0
		})
	}, [recordsAfterPagingAndSorting, cardSortBy, cardSortOrder])

	const CardSortIcon = ({ fieldId }) => {
		if (cardSortBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return cardSortOrder === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const CardSortPills = () => (
		<div className="flex items-center gap-1.5 flex-wrap">
			<span className="text-xs text-gray-400 mr-0.5">Sort:</span>
			{cardSortFields.map(field => (
				<button
					key={field.id}
					type="button"
					onClick={() => handleCardSort(field.id)}
					className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
						cardSortBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
					}`}
				>
					{field.label}
					<CardSortIcon fieldId={field.id} />
				</button>
			))}
		</div>
	)

	// Cards is desktop-only, so the option list is filtered rather than the JSX
	// branched — which mode exists on which viewport stays a data question.
	// Markup comes from the shared ViewToggle, byte-identical to the copy this
	// replaced, so desktop is unchanged.
	// Phones are List only — Cards and Map need the room — which leaves a single
	// option, so the toggle is not rendered at all rather than shown as a
	// one-button group. Same treatment as Projects and Portfolios. `listOnly` is
	// the phone cut-off (768px), not `isMobile` (1024px): a tablet keeps every
	// view (MYD-560).
	const viewOptions = [
		{ value: 'cards', label: 'Cards', icon: LayoutGrid },
		{ value: 'table', label: 'List', icon: List },
		{ value: 'map', label: 'Map', icon: Map },
	].filter(option => !listOnly || option.value === 'table')

	const showViewToggle = viewOptions.length > 1

	return (
		<div>
			<React.Fragment>
				{isLoading ? (
					<Spinner containerHeight="50vh" />
				) : !records ? (
					<Message variant="danger">{error}</Message>
				) : error ? (
					<Message variant="danger">{error}</Message>
				) : records.length === 0 ? (
					// Community/read-only accounts can't create — omit the CTA.
					<LocationsEmptyState onCreateLocation={canCreateAssets ? handelLocationAdd : undefined} />
				) : (
					<>
						<Layout.PageToolbarFilter
							buttonText={canCreateAssets ? 'Add Location' : undefined}
							buttonOnClick={canCreateAssets ? () => handelLocationAdd() : undefined}
							customButtons={
								showViewToggle ? (
									<div className="flex items-center gap-3">
										{/* Unreachable on mobile by construction: the mode is
										    coerced to 'table' before it reaches this component. */}
										{viewMode === 'cards' && <CardSortPills />}
										<ViewToggle
											value={viewMode}
											onChange={onViewModeChange}
											options={viewOptions}
										/>
									</div>
								) : undefined
							}
							searchInputLabel=""
							searchInputRef={searchInputRef}
							searchInputValue={searchInput}
							searchInputOnChange={handleSearchAll}
						/>

						{viewMode === 'map' ? (
							<>
								<LocationsMapPanel
									ref={mapRef}
									locations={mapLocations}
									onSelectLocation={handleSelectMapLocation}
									onGotoLocation={handleGotoMapLocation}
									initialView={savedMapView}
									showCards={false}
									showLayers={false}
								/>
								<LocationActionSheet
									location={sheetLocation}
									open={sheetOpen}
									onClose={() => setSheetOpen(false)}
								/>
							</>
						) : viewMode === 'table' ? (
							<LocationsTabelCN
								locations={recordsAfterPagingAndSorting()}
								openInMap={() => onViewModeChange('map')}
								recordCount={recordCount}
								userId={userId}
							/>
						) : (
							<LocationsCards locations={sortedCardLocations} recordCount={recordCount} userId={userId} />
						)}
					</>
				)}
			</React.Fragment>

			<Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
				<DialogContent className={addLocationMode === 'map' ? 'max-w-[920px]' : 'max-w-[700px]'}>
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<MapPin className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>{popupTitle}</DialogTitle>
								<DialogDescription>
									Search for a location, or place it on the map
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<LocationAddNewCN locationAdd={locationAdd} onModeChange={setAddLocationMode} />
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default Locations
